/**
 * Gestion du configurateur produit :
 * - Listes dépendantes (select avec data-depends-on) : options mises à jour selon le parent.
 * - Validation : require_all (tous les champs remplis) et max_identical (max N fois la même valeur).
 * Les valeurs sont envoyées en line_item_properties via le formulaire produit,
 * y compris l'ID variant numérique de chaque échantillon (_echantillon_X) sélectionné.
 */
(function () {
  const PLACEHOLDER_LABEL = 'Choisir…';
  // ID du produit "Discovery Set" pour lequel les options sont hydratées dynamiquement via Storefront API.
  const CUSTOM_DISCOVERY_SET_ID = 15566564688203;

  /**
   * Extrait l'identifiant numérique d'un variant depuis un GID Storefront API.
   * @param {string} gid
   * @returns {string}
   */
  function parseVariantIdFromGid(gid) {
    if (!gid || typeof gid !== 'string') return '';
    const match = gid.match(/ProductVariant\/(\d+)/);
    return match ? match[1] : '';
  }

  /**
   * Récupère la valeur d'un cookie par son nom.
   * @param {string} name
   * @returns {string}
   */
  function getCookieValue(name) {
    if (typeof document === 'undefined' || !document.cookie) return '';
    const escapedName = name.replace(/[-[\]/{}()*+?.\\^$|]/g, '\\$&');
    const match = document.cookie.match(new RegExp('(?:^|; )' + escapedName + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  /**
   * Récupère l'ID cart Storefront depuis le cookie "cart",
   * puis fallback sur les variables globales et le storage navigateur.
   * @returns {string}
   */
  function getCurrentStorefrontCartId() {
    if (typeof window === 'undefined') return '';

    const cartCookie = getCookieValue('cart');
    if (cartCookie) {
      if (cartCookie.indexOf('gid://shopify/Cart/') === 0) return cartCookie;
      return 'gid://shopify/Cart/' + cartCookie;
    }
    return '';
  }

  /**
   * Récupère les quantités de variants déjà présentes dans le panier Storefront courant.
   * @returns {Promise<Map<string, number>>}
   */
  async function getCurrentCartVariantQuantities() {
    const cartId = getCurrentStorefrontCartId();
    if (!cartId) return new Map();

    const query = `query CurrentCartLines($cartId: ID!) {
      cart(id: $cartId) {
        lines(first: 250) {
          nodes {
            quantity
            attributes {
              key
              value
            }
          }
        }
      }
    }`;

    const data = await StoreFrontAPI.fetch(query, { cartId: cartId });
    if (!data || !data.cart || !data.cart.lines || !Array.isArray(data.cart.lines.nodes)) {
      return new Map();
    }

    /**
     * Convertit une valeur d'attribut en ID variant numérique.
     * @param {string} rawValue
     * @returns {string}
     */
    function parseVariantIdFromAttributeValue(rawValue) {
      if (typeof rawValue !== 'string') return '';
      const value = rawValue.trim();
      if (!value) return '';
      const gidId = parseVariantIdFromGid(value);
      if (gidId) return gidId;
      const numericMatch = value.match(/^(\d+)$/);
      return numericMatch ? numericMatch[1] : '';
    }

    const quantitiesByVariant = new Map();
    data.cart.lines.nodes.forEach(function (line) {
      const lineQuantity = Number(line && line.quantity) || 0;
      if (lineQuantity <= 0) return;

      const attributes = line && Array.isArray(line.attributes) ? line.attributes : [];
      attributes.forEach(function (attribute) {
        const key = attribute && typeof attribute.key === 'string' ? attribute.key : '';
        const value = attribute && typeof attribute.value === 'string' ? attribute.value : '';
        if (!key || !value) return;

        const isSampleAttribute = key.indexOf('_echantillon_') === 0 || key === '_sample_variant_ids';
        if (!isSampleAttribute) return;

        const candidateValues = value.split(/[,\s;|]+/).filter(Boolean);
        candidateValues.forEach(function (candidate) {
          const variantId = parseVariantIdFromAttributeValue(candidate);
          if (!variantId) return;
          quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) || 0) + lineQuantity);
        });
      });
    });

    return quantitiesByVariant;
  }

  /**
   * Crée une option de select avec libellé, valeur et ID variant optionnel.
   * @param {{ value?: string, label?: string, variantId?: string, availableQuantity?: number }} opt
   * @returns {HTMLOptionElement}
   */
  function createSelectOption(opt) {
    const option = document.createElement('option');
    option.value = opt.value || '';
    option.textContent = opt.label != null ? opt.label : opt.value;
    if (opt.variantId) option.setAttribute('data-variant-id', String(opt.variantId));
    if (typeof opt.availableQuantity === 'number') {
      option.setAttribute('data-available-quantity', String(opt.availableQuantity));
    }
    return option;
  }

  /**
   * Met à jour les options d'un select dépendant selon la valeur du parent.
   */
  function updateDependentOptions(dependentSelect, optionsByParent, parentValue) {
    const options = optionsByParent && parentValue ? optionsByParent[parentValue] : null;
    dependentSelect.innerHTML = '';

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.disabled = true;
    placeholder.selected = true;
    placeholder.textContent = PLACEHOLDER_LABEL;
    dependentSelect.appendChild(placeholder);

    if (Array.isArray(options) && options.length > 0) {
      options.forEach(function (opt) {
        dependentSelect.appendChild(createSelectOption(opt));
      });
    }

    dependentSelect.value = '';
  }

  /**
   * Lit la config de validation depuis le script JSON dans le conteneur.
   * @param {HTMLElement} container
   * @returns {{ require_all?: boolean, max_identical?: number, identical_fields?: string[] }|null}
   */
  function getValidationConfig(container) {
    const script = container.querySelector('script[data-configurator-validation]');
    if (!script || !script.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  /**
   * Indique si un slot est en mode select unique (sans sous-options).
   * @param {HTMLElement} container
   * @param {string} slot
   * @returns {boolean}
   */
  function isSlotSingleSelect(container, slot) {
    const slotEl = container.querySelector('.product-configurator__slot[data-slot="' + slot + '"]');
    return slotEl ? slotEl.getAttribute('data-single-select') === 'true' : false;
  }

  /**
   * Mode combiné : met à jour les champs cachés.
   * Avec sous-options : value = "Marque - Parfum". Sans sous-options (select unique) : value = valeur du select.
   */
  /**
   * Met à jour les propriétés panier contenant l'ID variant de chaque échantillon sélectionné.
   * @param {HTMLElement} container
   */
  function syncVariantIdHiddenInputs(container) {
    const hiddenVariantInputs = container.querySelectorAll('input.product-configurator__hidden-variant-id');
    hiddenVariantInputs.forEach(function (hidden) {
      const slot = hidden.getAttribute('data-slot');
      const parfumSelect = container.querySelector(
        'select.product-configurator__select[data-slot="' + slot + '"][data-role="parfum"]'
      );
      if (!parfumSelect || !parfumSelect.value) {
        hidden.value = '';
        return;
      }
      const selectedOption = parfumSelect.options[parfumSelect.selectedIndex];
      const variantId = selectedOption && selectedOption.getAttribute('data-variant-id');
      hidden.value = variantId || '';
    });
  }

  /**
   * Met à jour les champs cachés envoyés au panier en mode "propriété combinée".
   * Le libellé envoyé reste lisible (ex: "Marque - Parfum") et l'ID variant est synchronisé en parallèle.
   * @param {HTMLElement} container
   */
  function syncCombinedHiddenInputs(container) {
    const hiddenInputs = container.querySelectorAll('input.product-configurator__hidden-slot');
    hiddenInputs.forEach(function (hidden) {
      const slot = hidden.getAttribute('data-slot');
      const marqueSelect = container.querySelector(
        'select.product-configurator__select[data-slot="' + slot + '"][data-role="marque"]'
      );
      const parfumSelect = container.querySelector(
        'select.product-configurator__select[data-slot="' + slot + '"][data-role="parfum"]'
      );
      const parfum = parfumSelect && parfumSelect.value ? parfumSelect.value.trim() : '';
      if (isSlotSingleSelect(container, slot)) {
        hidden.value = parfum;
      } else {
        const marque = marqueSelect && marqueSelect.value ? marqueSelect.value.trim() : '';
        hidden.value = marque && parfum ? marque + ' - ' + parfum : '';
      }
    });
    syncVariantIdHiddenInputs(container);
  }

  /**
   * Vérifie que tous les champs sont remplis (selects ou, en mode combiné, hidden inputs).
   * En mode combiné : slot avec sous-options = value contient " - " ; slot select unique = value non vide.
   */
  function allFieldsFilled(container) {
    if (container.getAttribute('data-combined-property') === 'true') {
      const hiddenInputs = container.querySelectorAll('input.product-configurator__hidden-slot');
      for (let i = 0; i < hiddenInputs.length; i++) {
        const val = hiddenInputs[i].value ? hiddenInputs[i].value.trim() : '';
        if (!val) return false;
        const slot = hiddenInputs[i].getAttribute('data-slot');
        if (!isSlotSingleSelect(container, slot) && val.indexOf(' - ') === -1) return false;
      }
      return true;
    }
    const selects = container.querySelectorAll('select.product-configurator__select');
    for (let i = 0; i < selects.length; i++) {
      if (!selects[i].value || selects[i].value.trim() === '') return false;
    }
    return true;
  }

  /**
   * Vérifie qu'aucune valeur (parfum) ne dépasse max_identical.
   * En mode combiné : slot avec sous-options = partie après " - " ; slot select unique = valeur entière.
   */
  function checkMaxIdentical(container, identicalFields, maxIdentical) {
    if (maxIdentical == null) return true;
    const counts = {};
    if (container.getAttribute('data-combined-property') === 'true') {
      const hiddenInputs = container.querySelectorAll('input.product-configurator__hidden-slot');
      hiddenInputs.forEach(function (input) {
        const v = input.value;
        const slot = input.getAttribute('data-slot');
        let partToCount = v;
        if (!isSlotSingleSelect(container, slot)) {
          const idx = v.indexOf(' - ');
          partToCount = idx !== -1 ? v.slice(idx + 3).trim() : v;
        }
        if (partToCount) counts[partToCount] = (counts[partToCount] || 0) + 1;
      });
    } else if (identicalFields && identicalFields.length) {
      identicalFields.forEach(function (key) {
        const name = 'properties[' + key + ']';
        const select = Array.from(container.querySelectorAll('select')).find(
          function (el) { return el.getAttribute('name') === name; }
        );
        if (select && select.value) {
          const v = select.value;
          counts[v] = (counts[v] || 0) + 1;
        }
      });
    } else {
      return true;
    }
    for (const count of Object.values(counts)) {
      if (count > maxIdentical) return false;
    }
    return true;
  }

  /**
   * Vérifie que le nombre de sélections d'un même variant ne dépasse pas sa quantité disponible calculée.
   * @param {HTMLElement} container
   * @returns {boolean}
   */
  function checkVariantAvailability(container) {
    const parfumSelects = container.querySelectorAll(
      'select.product-configurator__select[data-role="parfum"]'
    );
    const selectedCountByVariant = {};
    const availableQuantityByVariant = {};

    parfumSelects.forEach(function (select) {
      const selectedOption = select.options[select.selectedIndex];
      if (!selectedOption) return;
      const variantId = selectedOption.getAttribute('data-variant-id');
      if (!variantId) return;

      const availableRaw = selectedOption.getAttribute('data-available-quantity');
      const availableQuantity = availableRaw != null ? Number(availableRaw) : NaN;
      if (!Number.isFinite(availableQuantity)) return;

      selectedCountByVariant[variantId] = (selectedCountByVariant[variantId] || 0) + 1;
      availableQuantityByVariant[variantId] = availableQuantity;
    });

    // Chaque variant ne peut pas être sélectionné plus de fois que son stock restant calculé.
    return Object.keys(selectedCountByVariant).every(function (variantId) {
      return selectedCountByVariant[variantId] <= availableQuantityByVariant[variantId];
    });
  }

  /**
   * Récupère le conteneur du message d'erreur de validation.
   * @param {HTMLElement} container
   * @returns {HTMLElement|null}
   */
  function getErrorMessageEl(container) {
    return container.querySelector('.product-configurator__error-message');
  }

  /**
   * Met à jour l'état du bouton d'ajout au panier selon la validation et affiche/masque le message d'erreur.
   */
  function updateSubmitButton(container, validation) {
    const formId = container.getAttribute('data-product-form-id');
    if (!formId) return;
    const form = document.getElementById(formId);
    if (!form) return;
    const submitBtn = form.querySelector('button[type="submit"], [name="add"]');
    if (!submitBtn) return;

    const allFilled = !validation || validation.require_all !== true || allFieldsFilled(container);
    const needIdenticalCheck = validation && validation.max_identical != null &&
      (container.getAttribute('data-combined-property') === 'true' || (validation.identical_fields && validation.identical_fields.length));
    const identicalOk = !needIdenticalCheck || checkMaxIdentical(
      container,
      validation.identical_fields,
      validation.max_identical
    );
    const variantAvailabilityOk = checkVariantAvailability(container);

    const valid = allFilled && identicalOk && variantAvailabilityOk;
    submitBtn.disabled = !valid;
    submitBtn.setAttribute('aria-disabled', valid ? 'false' : 'true');

    const errorEl = getErrorMessageEl(container);
    if (errorEl) {
      const messageSpan = errorEl.querySelector('.product-form__error-message');
      if (valid) {
        errorEl.hidden = true;
        if (messageSpan) messageSpan.textContent = '';
      } else {
        errorEl.hidden = false;
        if (messageSpan) {
          if (!allFilled) {
            messageSpan.textContent = 'Veuillez remplir tous les champs pour ajouter au panier.';
          } else if (!identicalOk && validation && validation.max_identical != null) {
            messageSpan.textContent = "Un même parfum ne peut être choisi qu'au maximum " + validation.max_identical + ' fois.';
          } else if (!variantAvailabilityOk) {
            messageSpan.textContent = 'La quantité sélectionnée dépasse le stock disponible pour un ou plusieurs échantillons.';
          } else {
            messageSpan.textContent = 'La configuration n\'est pas valide.';
          }
        }
      }
    }
  }

  /**
   * Indique si la source de données a des sous-options (niveau marque + parfum).
   * @param {Object} dataSource
   * @returns {boolean}
   */
  function hasSubOptions(dataSource) {
    if (!dataSource.items || !Array.isArray(dataSource.items)) return false;
    return dataSource.items.some(function (item) {
      return item.option && item.option.items && item.option.items.length > 0;
    });
  }

  /**
   * Remplit les slots en mode structure+données :
   * - Avec sous-options : marque + parfum (data-options-by-parent), value = "Marque - Parfum".
   * - Sans sous-options (select unique) : un seul select rempli avec items, value = valeur choisie.
   * @param {HTMLElement} container
   * @param {Object} configData
   */
  function initStructureDataSlots(container, configData) {
    const slots = container.querySelectorAll('.product-configurator__slot[data-data-key]');
    slots.forEach(function (slotEl) {
      const dataKey = slotEl.getAttribute('data-data-key');
      if (!dataKey) return;
      const dataSource = configData[dataKey];
      if (!dataSource || !Array.isArray(dataSource.items)) return;

      const marqueSelect = slotEl.querySelector('select[data-role="marque"]');
      const parfumSelect = slotEl.querySelector('select[data-role="parfum"]');
      if (!marqueSelect || !parfumSelect) return;

      const withSubOptions = hasSubOptions(dataSource);

      if (withSubOptions) {
        const optionsByParent = {};
        dataSource.items.forEach(function (item) {
          optionsByParent[item.value] = (item.option && item.option.items) ? item.option.items : [];
        });
        parfumSelect.setAttribute('data-options-by-parent', JSON.stringify(optionsByParent));

        marqueSelect.innerHTML = '';
        const placeholderMarque = document.createElement('option');
        placeholderMarque.value = '';
        placeholderMarque.disabled = true;
        placeholderMarque.selected = true;
        placeholderMarque.textContent = (dataSource.default != null) ? dataSource.default : PLACEHOLDER_LABEL;
        marqueSelect.appendChild(placeholderMarque);
        dataSource.items.forEach(function (opt) {
          const option = document.createElement('option');
          option.value = opt.value || '';
          option.textContent = opt.label != null ? opt.label : opt.value;
          marqueSelect.appendChild(option);
        });
      } else {
        slotEl.setAttribute('data-single-select', 'true');
        marqueSelect.hidden = true;
        marqueSelect.removeAttribute('aria-label');
        parfumSelect.removeAttribute('data-depends-on');
        parfumSelect.removeAttribute('data-options-by-parent');
        var slotLabel = slotEl.querySelector('label');
        if (slotLabel && parfumSelect.id) slotLabel.setAttribute('for', parfumSelect.id);

        parfumSelect.innerHTML = '';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.disabled = true;
        placeholder.selected = true;
        placeholder.textContent = (dataSource.default != null) ? dataSource.default : PLACEHOLDER_LABEL;
        parfumSelect.appendChild(placeholder);
        dataSource.items.forEach(function (opt) {
          parfumSelect.appendChild(createSelectOption(opt));
        });
      }
    });
  }

  /**
   * Construit une source de données (marque -> parfums) à partir des produits groupés.
   * @param {Object} sourceData
   * @param {Map<string, { id: string, title: string, handle: string }[]>} productsGroupedByCollection
   * @returns {Object}
   */
  function buildDataSourceFromGroupedProducts(sourceData, productsGroupedByCollection) {
    const defaultBrand = sourceData && sourceData.default != null ? sourceData.default : 'Choisissez une marque';

    const firstOptionWithDefault = Array.isArray(sourceData && sourceData.items)
      ? sourceData.items.find(function (item) { return item && item.option && item.option.default; })
      : null;
    const defaultPerfume = firstOptionWithDefault && firstOptionWithDefault.option.default
      ? firstOptionWithDefault.option.default
      : 'Choisissez un parfum';

    const groupedItems = [];
    productsGroupedByCollection.forEach(function (products, groupNameRaw) {
      const groupName = typeof groupNameRaw === 'string' ? groupNameRaw.trim() : '';
      if (!groupName || !Array.isArray(products) || products.length === 0) return;

      const seenPerfumes = new Set();
      const perfumeItems = [];
      products.forEach(function (product) {
        const perfumeTitle = product && typeof product.title === 'string' ? product.title.trim() : '';
        if (!perfumeTitle || seenPerfumes.has(perfumeTitle)) return;
        seenPerfumes.add(perfumeTitle);
        perfumeItems.push({
          value: perfumeTitle,
          label: perfumeTitle,
          variantId: product.variantId || '',
          availableQuantity: typeof product.remainingQuantity === 'number' ? product.remainingQuantity : undefined,
        });
      });

      if (perfumeItems.length === 0) return;
      groupedItems.push({
        value: groupName,
        label: groupName,
        option: {
          default: defaultPerfume,
          type: 'select',
          items: perfumeItems,
        },
      });
    });

    groupedItems.sort(function (a, b) {
      return a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' });
    });

    return {
      ...sourceData,
      type: 'select',
      default: defaultBrand,
      items: groupedItems,
    };
  }

  /**
   * Hydrate toutes les sources de données "select" d'une configuration
   * avec les produits groupés récupérés via Storefront API.
   * @param {Object} configData
   * @param {Map<string, { id: string, title: string, handle: string }[]>} productsGroupedByCollection
   * @returns {Object}
   */
  function hydrateConfigDataFromProducts(configData, productsGroupedByCollection) {
    if (!configData || typeof configData !== 'object') return configData;
    const hydratedConfigData = {};

    Object.keys(configData).forEach(function (key) {
      const sourceData = configData[key];
      const isSelectableDataSource = sourceData &&
        sourceData.type === 'select' &&
        Array.isArray(sourceData.items);

      if (isSelectableDataSource) {
        hydratedConfigData[key] = buildDataSourceFromGroupedProducts(
          sourceData,
          productsGroupedByCollection
        );
      } else {
        hydratedConfigData[key] = sourceData;
      }
    });

    return hydratedConfigData;
  }

  /**
   * Compte le nombre de parfums distincts dans les produits groupés.
   * @param {Map<string, { id: string, title: string, handle: string }[]>} productsGroupedByCollection
   * @returns {number}
   */
  function countDistinctPerfumes(productsGroupedByCollection) {
    if (!productsGroupedByCollection || productsGroupedByCollection.size === 0) return 0;
    const distinctTitles = new Set();
    productsGroupedByCollection.forEach(function (products) {
      if (!Array.isArray(products)) return;
      products.forEach(function (product) {
        const title = product && typeof product.title === 'string' ? product.title.trim() : '';
        if (title) distinctTitles.add(title);
      });
    });
    return distinctTitles.size;
  }

  /**
   * Marque le produit comme indisponible côté interface.
   * @param {HTMLElement} container
   */
  function markProductUnavailable(container) {
    const formId = container.getAttribute('data-product-form-id');
    if (!formId) return;
    const form = document.getElementById(formId);
    if (!form) return;
    const submitBtn = form.querySelector('button[type="submit"], [name="add"]');
    if (!submitBtn) return;

    submitBtn.disabled = true;
    submitBtn.setAttribute('aria-disabled', 'true');
    submitBtn.classList.add('disabled');

    const buttonLabel = submitBtn.querySelector('span');
    if (buttonLabel) {
      buttonLabel.textContent = 'Non disponible';
    } else {
      submitBtn.textContent = 'Non disponible';
    }
  }

  /**
   * Initialise listes dépendantes + validation pour un conteneur configurateur.
   */
  async function initConfigurator(container) {
    const minimumDistinctPerfumes = 5;
    const structureDataScript = container.querySelector('script[data-configurator-data]');

    if (container.getAttribute('data-structure-data') === 'true' && structureDataScript && structureDataScript.textContent) {
      try {
        const configData = JSON.parse(structureDataScript.textContent);
        let resolvedConfigData = configData;

        try {
          // La logique dynamique (filtre stock + déduction du panier courant) ne s'applique
          // que sur le produit Discovery Set ; les autres produits conservent la config statique.
          if (ShopifyAnalytics.meta.product.id === CUSTOM_DISCOVERY_SET_ID) {
            const productsGroupedByCollection = await getProductsGroupedByCollection();
            if (productsGroupedByCollection.size === 0) {
              console.warn('Pas d\'échantillon disponible');
              markProductUnavailable(container);
              container.hidden = true;
              return;
            }

            const distinctPerfumesCount = countDistinctPerfumes(productsGroupedByCollection);
            if (distinctPerfumesCount === 0) {
              console.warn('Pas d\'échantillon disponible');
              markProductUnavailable(container);
              container.hidden = true;
              return;
            }

            if (distinctPerfumesCount < minimumDistinctPerfumes) {
              console.warn('Pas suffisamment d\'échantillons disponibles');
              markProductUnavailable(container);
              container.hidden = true;
              return;
            }
            resolvedConfigData = hydrateConfigDataFromProducts(configData, productsGroupedByCollection);
          }
          container.classList.remove('hidden');
        } catch (e) {
          /* En cas d'erreur API, on conserve la configuration d'origine du metafield */
        }

        initStructureDataSlots(container, resolvedConfigData);
      } catch (e) {
        /* ignorer si JSON invalide */
      }
    }

    const dependentSelects = container.querySelectorAll('select[data-depends-on]');

    dependentSelects.forEach(function (dependentSelect) {
      const dependsOnKey = dependentSelect.getAttribute('data-depends-on');
      if (!dependsOnKey) return;

      let optionsByParent = {};
      try {
        const raw = dependentSelect.getAttribute('data-options-by-parent');
        if (raw) optionsByParent = JSON.parse(raw);
      } catch (e) {
        return;
      }

      const isCombined = container.getAttribute('data-combined-property') === 'true';
      const slot = dependentSelect.getAttribute('data-slot');
      let parentSelect;
      if (isCombined && slot) {
        parentSelect = container.querySelector(
          'select.product-configurator__select[data-slot="' + slot + '"][data-role="marque"]'
        );
      } else {
        const parentName = 'properties[' + dependsOnKey + ']';
        parentSelect = Array.from(container.querySelectorAll('select')).find(
          function (el) { return el.getAttribute('name') === parentName; }
        );
      }
      if (!parentSelect) return;

      function onParentChange() {
        const parentValue = parentSelect.value || '';
        updateDependentOptions(dependentSelect, optionsByParent, parentValue);
        if (isCombined) syncCombinedHiddenInputs(container);
        const val = getValidationConfig(container);
        if (val) updateSubmitButton(container, val);
      }

      parentSelect.addEventListener('change', onParentChange);
      onParentChange();
    });

    const formId = container.getAttribute('data-product-form-id');
    if (formId) {
      const form = document.getElementById(formId);
      if (form) {
        form.addEventListener('submit', function () {
          if (container.getAttribute('data-combined-property') === 'true') {
            syncCombinedHiddenInputs(container);
          } else {
            syncVariantIdHiddenInputs(container);
          }
        });
      }
    }

    const validation = getValidationConfig(container);
    const isCombined = container.getAttribute('data-combined-property') === 'true';
    if (isCombined) {
      syncCombinedHiddenInputs(container);
      function onCombinedChange() {
        syncCombinedHiddenInputs(container);
        if (validation) updateSubmitButton(container, validation);
      }
      const selects = container.querySelectorAll('select.product-configurator__select');
      selects.forEach(function (select) {
        select.addEventListener('change', onCombinedChange);
      });
      if (validation) updateSubmitButton(container, validation);
    } else if (validation) {
      function runValidation() {
        updateSubmitButton(container, validation);
      }
      const selects = container.querySelectorAll('select.product-configurator__select');
      selects.forEach(function (select) {
        select.addEventListener('change', runValidation);
      });
      runValidation();
    }
  }

  function run() {
    // Initialisation de tous les configurateurs présents sur la page produit.
    document.querySelectorAll('[data-product-configurator]').forEach(initConfigurator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', run);
  }



  /**
   * Récupère les produits 2ml disponibles groupés par collection ou vendor (si pas dans une collection)
   * @returns {Promise<Map<string, { id: string, title: string, handle: string }[]>>} Map des collections avec les produits
   */
  async function getProductsGroupedByCollection() {
    let cursor = null;
    let hasNextPage = true;
    const query = `query ProductsGlobal(\$cursor: String) {
      products(first: 100, after: \$cursor, query: \"title:*2ml* AND available_for_sale:true\") {
        pageInfo { hasNextPage endCursor }
        nodes {
          id title handle availableForSale vendor
          variants(first: 1) { nodes { id quantityAvailable } }
          collections(first: 20) { nodes { title } }
        }
      }}`;

    const grouped = new Map();
    let cartVariantQuantities = new Map();
    try {
      // Quantités déjà "réservées" dans le panier courant, lues via les attributs _echantillon_*.
      cartVariantQuantities = await getCurrentCartVariantQuantities();
    } catch (e) {
      /* Si la lecture du panier Storefront échoue, on n'applique pas la soustraction */
      cartVariantQuantities = new Map();
    }

    /**
     * Normalise le libellé parfum en retirant le suffixe " - 2ml".
     * @param {string} rawTitle
     * @returns {string}
     */
    function normalizePerfumeTitle(rawTitle) {
      if (typeof rawTitle !== 'string') return '';
      return rawTitle
        .replace(/\s[-–]\s*2ml\s*$/i, '')
        .trim();
    }

    while (hasNextPage) {
      const data = await StoreFrontAPI.fetch(query, { cursor });
      const { nodes, pageInfo } = data.products;
      for (const product of nodes) {
        const normalizedTitle = normalizePerfumeTitle(product.title);
        if (!normalizedTitle) continue;
        const variantNode = product.variants && product.variants.nodes && product.variants.nodes[0];
        const quantityAvailable = variantNode && typeof variantNode.quantityAvailable === 'number'
          ? variantNode.quantityAvailable
          : 0;
        const variantId = variantNode ? parseVariantIdFromGid(variantNode.id) : '';
        const quantityReservedInCart = variantId ? (cartVariantQuantities.get(variantId) || 0) : 0;
        // Pour les variants sans gestion de stock, Shopify peut renvoyer quantityAvailable <= 0
        // tout en laissant availableForSale à true. Infinity évite de les exclure à tort.
        const remainingQuantity = (quantityAvailable <= 0 ? Infinity : quantityAvailable) - quantityReservedInCart;
        if (remainingQuantity <= 0) continue;
        const collections = product.collections && Array.isArray(product.collections.nodes)
          ? product.collections.nodes
          : [];

        if (collections.length === 0) {
          const vendorName = product.vendor && String(product.vendor).trim()
            ? String(product.vendor).trim()
            : 'Sans marque';
          if (!grouped.has(vendorName)) grouped.set(vendorName, []);
          grouped.get(vendorName).push({
            id: product.id,
            title: normalizedTitle,
            handle: product.handle,
            variantId: variantId,
            remainingQuantity: remainingQuantity,
          });
          continue;
        }

        collections.forEach(function (collection) {
          const collectionName = collection && typeof collection.title === 'string' ? collection.title.trim() : '';
          if (!collectionName) return;
          if (!grouped.has(collectionName)) grouped.set(collectionName, []);
          grouped.get(collectionName).push({
            id: product.id,
            title: normalizedTitle,
            handle: product.handle,
            variantId: variantId,
            remainingQuantity: remainingQuantity,
          });
        });
      }

      grouped.forEach(function (products, groupName) {
        const seenTitles = new Set();
        const uniqueProducts = [];
        products.forEach(function (item) {
          const key = item.title;
          if (!key || seenTitles.has(key)) return;
          seenTitles.add(key);
          uniqueProducts.push(item);
        });
        grouped.set(groupName, uniqueProducts);
      });
      hasNextPage = pageInfo.hasNextPage;
      cursor = pageInfo.endCursor;
    }
    return grouped;
  }
})();
